import { Component, Input, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Affinity, MAX_AFFINITIES } from '../../../../../core/models/editorialContent.model';
import { GroupIconComponent } from '../../../../../shared/components/group-icon/group-icon.component';

/** One tile: the family term, the visual group it belongs to, and the name of that group. */
interface RenderedAffinity {
  code:      string;
  label:     string;
  iconKey:   string;
  /** Localised name of the visual group, glossing its glyph as in the character tiles. */
  groupName: string;
}

/**
 * Gastronomic affinities.
 *
 * The wording is deliberate and is a product boundary, not copy polish: the sheet says the
 * spirit GOES WELL WITH a universal food family. It never states that the venue serves that
 * dish, has it available, or serves the bottle in any particular way — glassware, ice, garnish,
 * temperature and cocktails belong to the establishment, not to a global catalogue.
 *
 * The intro line carries that promise, so the chips can never be read as an offer on their own.
 */
@Component({
  selector: 'app-spirit-affinities',
  standalone: true,
  imports: [TranslateModule, GroupIconComponent],
  templateUrl: './spirit-affinities.component.html',
  styleUrl: './spirit-affinities.component.scss'
})
export class SpiritAffinitiesComponent {

  private translate = inject(TranslateService);
  private languageChange = toSignal(this.translate.onLangChange);

  private source = signal<Affinity[]>([]);

  @Input({ required: true }) set affinities(value: Affinity[] | null | undefined) {
    this.source.set(value ?? []);
  }

  visible = computed<RenderedAffinity[]>(() => {
    this.languageChange();

    return this.source().slice(0, MAX_AFFINITIES).map(affinity => ({
      code:      affinity.code,
      label:     affinity.label,
      iconKey:   affinity.iconKey,
      groupName: this.groupNameOf(affinity.iconKey)
    }));
  });

  /**
   * Same rule as the character tiles: a group with no translation yet renders without a caption
   * rather than with a raw key. The term is the information, the family is the gloss, and a
   * gloss reading "shellfish_new" is worse than no gloss at all.
   */
  private groupNameOf(iconKey: string): string {
    const key = 'editorial.group.' + iconKey;
    const translated = this.translate.instant(key);

    return translated && translated !== key ? translated : '';
  }
}
